import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface TestimonialItem {
  name: string;
  role: string;
  location: string;
  text: string;
}

const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const testimonialsData = [
    {
      id: 1,
      avatar: 'https://readdy.ai/api/search-image?query=latina%20woman%20entrepreneur%20smiling%20professional%20headshot%20warm%20background&width=80&height=80&seq=test1&orientation=squarish',
      stars: 5,
    },
    {
      id: 2,
      avatar: 'https://readdy.ai/api/search-image?query=latino%20man%20fitness%20coach%20professional%20headshot%20confident%20smile&width=80&height=80&seq=test2&orientation=squarish',
      stars: 5,
    },
    {
      id: 3,
      avatar: 'https://readdy.ai/api/search-image?query=latina%20businesswoman%20consultant%20professional%20headshot%20modern%20office&width=80&height=80&seq=test3&orientation=squarish',
      stars: 5,
    },
    {
      id: 4,
      avatar: 'https://readdy.ai/api/search-image?query=latino%20restaurant%20owner%20man%20professional%20headshot%20friendly%20smile&width=80&height=80&seq=test4&orientation=squarish',
      stars: 5,
    },
    {
      id: 5,
      avatar: 'https://readdy.ai/api/search-image?query=young%20latina%20content%20creator%20influencer%20professional%20headshot%20colorful%20background&width=80&height=80&seq=test5&orientation=squarish',
      stars: 5,
    },
  ];

  // Merge translation data with static data
  const i18nItems = t('home.testimonials.items', { returnObjects: true }) as TestimonialItem[];
  const testimonials = testimonialsData.map((item, index) => ({
    ...item,
    ...i18nItems[index],
  }));

  const prev = () => setActive((a) => (a === 0 ? testimonials.length - 1 : a - 1));
  const next = () => setActive((a) => (a === testimonials.length - 1 ? 0 : a + 1));

  const getVisible = () => {
    const indices = [];
    for (let i = -1; i <= 1; i++) {
      indices.push((active + i + testimonials.length) % testimonials.length);
    }
    return indices;
  };

  return (
    <section className="relative py-24 bg-[#030006] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(140,82,255,0.08)_0%,transparent_70%)]" />

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#8c52ff] text-xs font-bold uppercase tracking-widest mb-3">{t('home.testimonials.badge')}</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4">
            {t('home.testimonials.titlePrefix')}{' '}
            <span className="bg-gradient-to-r from-[#8c52ff] to-[#FF2DAA] bg-clip-text text-transparent">
              {t('home.testimonials.titleHighlight')}
            </span>
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('home.testimonials.subtitle')}
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          <div className="flex items-center justify-center gap-4 md:gap-6">
            {getVisible().map((idx, pos) => {
              const tItem = testimonials[idx];
              const isCenter = pos === 1;
              return (
                <div
                  key={tItem.id}
                  className={`transition-all duration-500 ${
                    isCenter
                      ? 'scale-100 opacity-100 z-10 flex-1 max-w-lg'
                      : 'scale-90 opacity-40 hidden md:block flex-1 max-w-sm'
                  }`}
                >
                  <div
                    className={`bg-white/3 backdrop-blur-sm border rounded-2xl p-6 md:p-8 h-full ${
                      isCenter ? 'border-[#8c52ff]/30' : 'border-white/8'
                    }`}
                  >
                    {/* Stars */}
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: tItem.stars }).map((_, i) => (
                        <i key={i} className="ri-star-fill text-yellow-400 text-sm" />
                      ))}
                    </div>

                    {/* Quote */}
                    <i className="ri-double-quotes-l text-3xl text-[#8c52ff]/30 mb-3 block" />
                    <p className="text-white/70 text-sm md:text-base leading-relaxed mb-6 italic">
                      &ldquo;{tItem.text}&rdquo;
                    </p>

                    {/* Author */}
                    <div className="flex items-center gap-3">
                      <img
                        src={tItem.avatar}
                        alt={tItem.name}
                        className="w-12 h-12 rounded-full object-cover object-top border-2 border-[#8c52ff]/30"
                      />
                      <div>
                        <div className="text-white font-bold text-sm">{tItem.name}</div>
                        <div className="text-white/40 text-xs">{tItem.role}</div>
                        <div className="text-[#8c52ff] text-xs">{tItem.location}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-10">
            <button
              onClick={prev}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/15 text-white hover:bg-[#8c52ff]/20 hover:border-[#8c52ff]/40 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
            </button>

            <div className="flex items-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    i === active
                      ? 'w-6 h-2 bg-gradient-to-r from-[#8c52ff] to-[#00c2ff]'
                      : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={next}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/5 border border-white/15 text-white hover:bg-[#8c52ff]/20 hover:border-[#8c52ff]/40 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
